import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe, KeyValuePipe, DecimalPipe, JsonPipe } from '@angular/common';
import { BackofficeService } from '@services/backoffice/backoffice.service';
import { RoutineAggregate, RoutineExecutionDetailResponse } from '@interfaces/backoffice';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzBadgeModule } from 'ng-zorro-antd/badge';

@Component({
  selector: 'app-routines-monitor',
  standalone: true,
  imports: [
    FormsModule,
    DatePipe,
    KeyValuePipe,
    DecimalPipe,
    JsonPipe,
    NzTableModule,
    NzButtonModule,
    NzInputModule,
    NzTagModule,
    NzIconModule,
    NzModalModule,
    NzCardModule,
    NzStatisticModule,
    NzSpinModule,
    NzDividerModule,
    NzCollapseModule,
    NzToolTipModule,
    NzBadgeModule,
  ],
  templateUrl: './routines-monitor.component.html',
  styleUrl: './routines-monitor.component.scss',
})
export class RoutinesMonitorComponent implements OnInit {
  private readonly svc = inject(BackofficeService);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);

  loading = signal(false);
  aggregates = signal<RoutineAggregate[]>([]);
  queueStatus = signal<Record<string, number>>({});

  enqueuePatientId = '';
  processLimit = 10;

  // Step panel state
  expandedPatientId = signal<string | null>(null);
  stepDetail = signal<RoutineExecutionDetailResponse | null>(null);
  stepDetailLoading = signal(false);
  // Cache: patientId -> detail (avoid re-fetching)
  private readonly stepCache = new Map<string, RoutineExecutionDetailResponse>();

  ngOnInit(): void {
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.svc.routineQueueStatus().subscribe({
      next: (res) => this.queueStatus.set(res.queue_status),
      error: () => this.message.error('Failed to load queue status'),
    });
    this.svc.routineAggregates(100).subscribe({
      next: (res) => {
        this.aggregates.set(res.aggregates);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.message.error('Failed to load routine aggregates');
      },
    });
  }

  enqueue(): void {
    const id = this.enqueuePatientId.trim();
    if (!id) return;
    this.svc.routineEnqueue(id).subscribe({
      next: (res) => {
        this.message.success(res.message);
        this.enqueuePatientId = '';
        this.refresh();
      },
      error: (err) => this.message.error(err.error?.message || 'Enqueue failed'),
    });
  }

  requeue(patientId: string): void {
    this.svc.routineRequeue(patientId).subscribe({
      next: (res) => {
        this.message.success(res.message);
        this.refresh();
      },
      error: (err) => this.message.error(err.error?.message || 'Requeue failed'),
    });
  }

  stop(patientId: string): void {
    this.svc.routineStop(patientId).subscribe({
      next: (res) => {
        this.message.success(res.message);
        this.refresh();
      },
      error: (err) => this.message.error(err.error?.message || 'Stop failed'),
    });
  }

  processQueue(): void {
    this.svc.routineProcessQueue(this.processLimit).subscribe({
      next: (res) => {
        this.message.success(res.message);
        this.refresh();
      },
      error: (err) => this.message.error(err.error?.message || 'Process queue failed'),
    });
  }

  viewLatest(patientId: string): void {
    this.svc.routineLatest(patientId).subscribe({
      next: (detail) => {
        this.modal.info({
          nzTitle: `Latest Routine for ${patientId.slice(0, 8)}...`,
          nzContent: `<pre style="max-height:400px;overflow:auto;font-size:12px">${JSON.stringify(detail, null, 2)}</pre>`,
          nzWidth: 720,
        });
      },
      error: () => this.message.error('Failed to load routine detail'),
    });
  }

  /** Toggle the step-by-step execution panel for a patient row. */
  toggleSteps(patientId: string): void {
    if (this.expandedPatientId() === patientId) {
      // Collapse
      this.expandedPatientId.set(null);
      this.stepDetail.set(null);
      return;
    }
    this.expandedPatientId.set(patientId);

    // Use cache if available
    const cached = this.stepCache.get(patientId);
    if (cached) {
      this.stepDetail.set(cached);
      return;
    }

    this.stepDetailLoading.set(true);
    this.stepDetail.set(null);
    this.svc.routineExecutionDetail(patientId).subscribe({
      next: (detail) => {
        this.stepCache.set(patientId, detail);
        this.stepDetail.set(detail);
        this.stepDetailLoading.set(false);
      },
      error: () => {
        this.stepDetailLoading.set(false);
        this.message.error('Failed to load execution detail');
      },
    });
  }

  statusColor(status: string): string {
    switch (status) {
      case 'completed': return 'green';
      case 'processing': return 'blue';
      case 'queued': return 'orange';
      case 'failed': return 'red';
      case 'not_started': return 'default';
      default: return 'default';
    }
  }

  confidenceColor(score: number | null): string {
    if (score == null) return 'default';
    if (score >= 0.8) return 'green';
    if (score >= 0.6) return 'orange';
    return 'red';
  }

  scorePercent(score: number | null): string {
    if (score == null) return '—';
    return `${Math.round(score * 100)}%`;
  }
}
